// src/features/streams/components/StreamGridRow.tsx

import { memo, type CSSProperties, type MouseEvent } from 'react';
import { IconDots } from '@tabler/icons-react';
import type { StreamGridRow as StreamGridRowData } from '@/lib/types/Stream';
import styles from './StreamGrid.module.css';

interface StreamGridRowProps {
  stream: StreamGridRowData;
  index: number;
  isSelected: boolean;
  onSelectOnly: (id: number, index: number) => void;
  onToggle: (id: number, index: number) => void;
  onRangeSelect: (id: number, index: number) => void;
  onMenuOpen: (index: number, buttonEl: HTMLElement) => void;
  onNavigate: (href: string) => void;
  style: CSSProperties;
}

/**
 * Single flat row component — all content inlined as native HTML elements.
 * Zero Mantine/Router component instances per row for minimal reconciliation cost.
 * The shared Menu component lives in StreamGrid (single instance).
 */
function StreamGridRowComponent({
  stream,
  index,
  isSelected,
  onSelectOnly,
  onToggle,
  onRangeSelect,
  onMenuOpen,
  onNavigate,
  style,
}: StreamGridRowProps) {
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // Checkbox click - toggle selection
    if (target.closest('[data-cell="checkbox"]')) {
      onToggle(stream.mediaId, index);
      return;
    }

    // Actions button click - let the button handler deal with it
    if (target.closest('[data-cell="actions"]')) {
      return;
    }

    // Title link click - let it handle navigation
    if (target.closest('[data-cell="title"] a')) {
      return;
    }

    // Row selection with modifier keys
    if (e.shiftKey) {
      e.preventDefault();
      onRangeSelect(stream.mediaId, index);
    } else if (e.ctrlKey || e.metaKey) {
      onToggle(stream.mediaId, index);
    } else {
      onSelectOnly(stream.mediaId, index);
    }
  };

  const handleTitleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Allow right-click / middle-click to open in new tab (native <a> behavior)
    // Left-click does client-side navigation
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onNavigate(stream.href);
    }
  };

  // Determine live status CSS classes
  const dotClass = stream.liveStatus === 'recording'
    ? styles.statusDotRecording
    : stream.liveStatus === 'live'
      ? styles.statusDotLive
      : styles.statusDotOffline;

  const labelClass = stream.liveStatus === 'offline'
    ? styles.liveStatusLabelOffline
    : styles.liveStatusLabelActive;

  const liveLabel = stream.liveStatus === 'recording'
    ? 'Live (Rec)'
    : stream.liveStatus === 'live'
      ? 'Live'
      : 'Offline';

  return (
    <div
      className={`${styles.row} ${styles.gridColumns} ${isSelected ? styles.rowSelected : ''}`}
      style={style}
      onClick={handleClick}
      data-selected={isSelected}
      data-stream-grid-row="true"
    >
      {/* Checkbox — CSS-only custom checkbox */}
      <div className={`${styles.cell} ${styles.cellCheckbox}`} data-cell="checkbox">
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={isSelected}
            readOnly
            tabIndex={-1}
            aria-label={`Select ${stream.title}`}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.checkboxControl} />
        </label>
      </div>

      {/* Thumbnail — poster-derived thumb, same treatment as the Videos grid */}
      <div className={`${styles.cell} ${styles.cellThumbnail}`} data-col="thumbnail">
        <div className={styles.thumbContainer}>
          {stream.thumbnailUrl ? (
            <img
              src={stream.thumbnailUrl}
              alt={stream.title}
              className={styles.thumbImage}
              loading="lazy"
            />
          ) : (
            <div className={styles.thumbPlaceholder}>
              <span className={styles.thumbPlaceholderText}>N/A</span>
            </div>
          )}
        </div>
      </div>

      {/* Title + display ID + stream type badge */}
      <div className={`${styles.cell} ${styles.cellTitle}`} data-cell="title">
        <div className={styles.titleContainer}>
          <a href={stream.href} className={styles.titleLink} onClick={handleTitleClick}>
            {stream.title}
          </a>
          <div className={styles.titleSubline}>
            <span className={styles.titleId}>{stream.displayId}</span>
            <span className={styles.streamTypeBadge}>{stream.streamTypeLabel}</span>
          </div>
        </div>
      </div>

      {/* Live Status — colored dot + label */}
      <div className={`${styles.cell} ${styles.cellLiveStatus}`} data-col="liveStatus">
        <span className={styles.liveIndicator}>
          <span className={`${styles.statusDot} ${dotClass}`} />
          <span className={`${styles.liveStatusLabel} ${labelClass}`}>{liveLabel}</span>
        </span>
      </div>

      {/* Location */}
      <div className={`${styles.cell} ${styles.cellLocation}`} data-col="location">
        {stream.locationLabel}
      </div>

      {/* Status — CSS-only badge */}
      <div className={`${styles.cell} ${styles.cellStatus}`} data-col="status">
        <span className={styles.statusBadge} data-color={stream.statusColor}>
          {stream.statusLabel}
        </span>
      </div>

      {/* Published */}
      <div className={`${styles.cell} ${styles.cellDate}`} data-col="published">
        {stream.publishedFormatted ? (
          <span title={stream.publishedTooltip ?? undefined}>{stream.publishedFormatted}</span>
        ) : '-'}
      </div>

      {/* Updated */}
      <div className={`${styles.cell} ${styles.cellDate}`} data-col="updated">
        <span title={stream.updatedTooltip}>{stream.updatedFormatted}</span>
      </div>

      {/* Kebab — keep IconDots (trivial SVG, no hooks) */}
      <div className={`${styles.cell} ${styles.cellAction}`} data-cell="actions">
        <button
          className={styles.kebabButton}
          onClick={(e) => { e.stopPropagation(); onMenuOpen(index, e.currentTarget); }}
          aria-label="Actions"
          type="button"
        >
          <IconDots size={16} />
        </button>
      </div>
    </div>
  );
}

// Memoize row — virtualization reuses row slots, comparator keeps updates focused.
export const StreamGridRow = memo(StreamGridRowComponent, (prev, next) => {
  return (
    prev.stream === next.stream &&
    prev.index === next.index &&
    prev.isSelected === next.isSelected &&
    prev.style.transform === next.style.transform &&
    prev.style.height === next.style.height
  );
});
